<template>
  <div v-if="!loadingState" class="d-flex flex-column h-100" data-cy="dashboard-content">
    <navigation-top/>
    <template v-if="subscriptionAccessType !== 'NO_ACCESS'">
      <div style="margin-top: 37px;">
        <!-- no project and no organisation -->
        <div v-if="!hasOrganisations">
          <div class="mb-5">Create your first organisation</div>

          <div class="dashboard-grid-layout mb-6">
            <settings-organisation-dialog>
              <template #activator="{ props }">
                <div v-bind="props"
                     class="dashboard-card text-new-button d-flex flex-column justify-center align-center">
                  <v-icon style="font-size: 40px;" class="mb-2">mds:add</v-icon>
                  New Organisation
                </div>
              </template>
            </settings-organisation-dialog>
          </div>
        </div>

        <template v-if="hasRecentWorkflows">
          <div class="d-flex justify-space-between mb-5">
            <span>Recent workflows</span>
            <div class="d-flex ga-5">
              <sort-dropdown
                :items="filteringOptions"
                :title="''"
                :default-value="workflowsFilterBy"
                @onSelect="handleFilterSelected($event, false)"
              />

              <sort-dropdown
                :items="sortingOptions"
                :default-value="workflowsSortBy"
                @onSelect="handleSortSelected($event, false)"
              />
            </div>
          </div>

          <div class="dashboard-grid-layout mb-6">
            <workflow-dialog
              v-if="hasProjects && subscriptionAccessType === 'FULL'"
              :setWorkflow="true"
              ref="dialogWorkflow"
              :memberships="filteredProjectMemberships"
              @onsave="fetchAndUpdateWorkflowsWithSummary()"
            >
              <template #activator="{ props }">
                <div v-bind="props"
                     class="dashboard-card text-new-button d-flex flex-column justify-center align-center">
                  <v-icon style="font-size: 40px;" class="mb-2">mds:add</v-icon>
                  New Workflow
                </div>
              </template>
            </workflow-dialog>
            <div
              v-for="workflow in filteredAndSortedRecentWorkflowsWithData"
              :key="workflow?.id"
            >
              <dashboard-workflow-card
                v-if="workflow" :workflow="workflow"
                :hideActions="!workflow?.isTeamMember || subscriptionAccessType !== 'FULL'"
                @edit="editWorkflow"
              ></dashboard-workflow-card>
            </div>
          </div>
        </template>
      </div>

      <settings-project-dialog
        ref="dialogProject"
        :setProject="false"
      >
        <template #activator><span></span></template>
      </settings-project-dialog>

      <!-- available projects -->
      <div v-if="hasProjects" :class="{'dashboard-container-alt' : hasRecentWorkflows }" style="flex: 1 1 auto;"
           data-cy="dashboard-recent-projects">
        <div class="d-flex justify-space-between mb-5">
          <span>All projects</span>
          <div class="d-flex ga-5">
            <sort-dropdown
              :items="filteringOptions"
              :title="''"
              :default-value="projectsFilterBy"
              @onSelect="handleFilterSelected($event, true)"
            />

            <sort-dropdown
              :items="projectSortingOptions"
              :default-value='projectsSortBy'
              @onSelect="handleSortSelected($event, true)"
            />
          </div>
        </div>

        <div class="dashboard-grid-layout mb-6">
          <div v-if="loadingActiveSubscription" class="dashboard-card dashboard-card-placeholder"></div>
          <settings-project-dialog
            v-else-if="selectedPlan && projectMemberships.length < selectedPlan.maxProjectsCount && subscriptionAccessType === 'FULL'"
            :setProject="true"
          >
            <template #activator="{ props }">
              <div
                v-bind="props"
                class="dashboard-card text-new-button d-flex flex-column justify-center align-center"
              >
                <v-icon style="font-size: 40px;" class="mb-2">mds:add</v-icon>
                New Project
              </div>
            </template>
          </settings-project-dialog>
          <div
            v-else-if="activeSubscription"
            class="dashboard-card text-new-button d-flex flex-column justify-center align-center bg-deep-purple-accent-2 border-none cursor-not-allowed"
          >
            Upgrade to start using {{ brand.name }}
          </div>
          <div
            v-for="membership in filteredAndSortedProjectMemberships"
            :key="membership.id"
          >
            <dashboard-project-card
              :project="membership.project"
              :hideActions="true"
              :disabled="membership.disabled || (!loadingActiveSubscription && subscriptionAccessType !== 'FULL')"
              :noAccess="membership.disabled"
              @set-project="setProject"
              @edit-project="editItemProject(membership.project)"
            ></dashboard-project-card>
          </div>
        </div>
      </div>

      <div
        v-if="hasOrganisations && !hasProjects"
        :class="{'dashboard-container-alt' : hasRecentWorkflows}"
        style="flex: 1 1 auto;"
      >
        <div class="mb-5">Projects</div>
        <div class="dashboard-grid-layout mb-6">
          <settings-project-dialog
            :setProject="true"
          >
            <template #activator="{ props }">
              <div v-bind="props" class="dashboard-card text-new-button d-flex flex-column justify-center align-center">
                <v-icon style="font-size: 40px;" class="mb-2">mds:add</v-icon>
                New Project
              </div>
            </template>
          </settings-project-dialog>
        </div>
      </div>
    </template>
    <welcome-to-plan-dialog
      :is-open="welcomeToPlanDialog"
      :plan="welcomeToPlanName"
      @close="welcomeToPlanDialog = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict'
import SortDropdown from '@/components/SortDropdown'
import NavigationTop from '@/components/NavigationTop'
import WorkflowDialog from '@/components/WorkflowDialog'
import WelcomeToPlanDialog from '@/components/WelcomeToPlanDialog'
import DashboardProjectCard from '@/components/DashboardProjectCard'
import DashboardWorkflowCard from '@/components/DashboardWorkflowCard'
import SettingsProjectDialog from '@/components/SettingsProjectDialog'
import SettingsOrganisationDialog from '@/components/SettingsOrganisationDialog'
import { EventBus } from '@/event-bus/event-bus.js'
import recentWorkflowsApi from '@/api/recentWorkflowsApi'
import { readStorage, writeStorage } from '@/utils/storage'
import { getEntitledPlan, getPlanByPriceId } from '@/utils/payments'
import { sortingOptions, projectSortingOptions, filteringOptions } from '@/utils/constants'
import brand from '@/composables/useBrand'

const WORKFLOWS_SORT_STORAGE_KEY = 'qlerify.dashboard.workflowsSortBy'
const PROJECTS_SORT_STORAGE_KEY = 'qlerify.dashboard.projectsSortBy'
const WORKFLOWS_FILTER_STORAGE_KEY = 'qlerify.dashboard.workflowsFilterBy'
const PROJECTS_FILTER_STORAGE_KEY = 'qlerify.dashboard.projectsFilterBy'

export default {
  components: {
    SortDropdown,
    SettingsProjectDialog,
    SettingsOrganisationDialog,
    WorkflowDialog,
    DashboardWorkflowCard,
    DashboardProjectCard,
    NavigationTop,
    WelcomeToPlanDialog
  },
  data () {
    return {
      workflowsFilterBy: readStorage(WORKFLOWS_FILTER_STORAGE_KEY, 'owned_by_anyone'),
      workflowsSortBy: readStorage(WORKFLOWS_SORT_STORAGE_KEY, 'last_modified'),
      projectsFilterBy: readStorage(PROJECTS_FILTER_STORAGE_KEY, 'owned_by_anyone'),
      projectsSortBy: readStorage(PROJECTS_SORT_STORAGE_KEY, 'last_opened'),
      sortBy: [{ key: 'lastLogin', order: 'desc' }],
      headersProjects: [
        {
          title: 'Organisation',
          align: 'left',
          sortable: true,
          value: 'project.organisation.name'
        },
        {
          title: 'Project',
          align: 'left',
          sortable: true,
          value: 'project.name'
        },
        {
          title: 'Owner',
          align: 'left',
          sortable: false
        },
        {
          title: 'Last seen',
          align: 'left',
          sortable: true,
          value: 'lastLogin',
          key: 'lastLogin'
        },
        {
          title: '',
          align: 'center',
          sortable: false
        }
      ],
      recentWorkflowsWithData: [],
      sortingOptions,
      projectSortingOptions,
      filteringOptions,
      isFirstRun: true,
      welcomeToPlanName: '',
      welcomeToPlanDialog: false
    }
  },
  methods: {
    formatDistanceToNowStrict,
    setProject (id) {
      EventBus.$emit('set-project', { id })
    },
    editItemProject (item) {
      this.$refs.dialogProject.setItem(Object.assign({}, item))
      this.$refs.dialogProject.openProjectDialog()
    },
    editWorkflow (item) {
      this.$refs.dialogWorkflow.setItem(Object.assign({}, item))
      this.$refs.dialogWorkflow.setProject(item.projectId)
      this.$refs.dialogWorkflow.openWorkflowDialog()
    },
    async fetchAndUpdateWorkflowsWithSummary () {
      this.$loadingState.start()
      for (const index in this.recentWorkflows) {
        const it = this.recentWorkflows[index]
        const workflow = await recentWorkflowsApi.getWorkflowSummary(it, it.projectName)
        if (workflow) {
          const isTeamMember = !!this.currentUser && workflow?.teamMembers?.findIndex(member => member.userId === this.currentUser.id) !== -1
          this.recentWorkflowsWithData[index] = {
            ...workflow,
            organisationName: it.organisationName,
            isTeamMember
          }
        }
      }
      this.$loadingState.stop()
    },
    getProjectOwner (projectId) {
      const membership = this.projectMemberships?.find(pm => pm.project?.id === projectId)
      return membership?.project?.organisation?.userId
    },
    isProjectOwner (projectId) {
      const projectOwnerId = this.getProjectOwner(projectId)
      return this.currentUser?.id === projectOwnerId
    },
    handleSortSelected (value, isProject) {
      if (isProject) {
        this.projectsSortBy = value
      } else {
        this.workflowsSortBy = value
      }
    },
    handleFilterSelected (value, isProject) {
      if (isProject) {
        this.projectsFilterBy = value
      } else {
        this.workflowsFilterBy = value
      }
    }
  },
  computed: {
    brand: () => brand,
    ...mapState({
      projectMemberships: state => state.projectMemberships.projectMemberships,
      organisations: state => state.organisations.organisations,
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription,
      isEnterpriseUser: state => state.payments.isEnterpriseUser,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription
    }),
    ...mapGetters({
      loadingState: 'loading/getLoadingState',
      projects: 'projectMemberships/myProjects'
    }),
    selectedPlan: function () {
      return getEntitledPlan(this.activeSubscription)
    },
    lastSelectedPlan () {
      try {
        return getPlanByPriceId(this.activeSubscription?.lastSubscriptionDetails?.price?.id)
      } catch (error) {
        return null
      }
    },
    projectMembershipsWithAccess () {
      // Fetch project memberships asynchronously
      const projectMemberships = this.projectMemberships
      if (!projectMemberships) {
        return []
      }

      // Fetch the active plan asynchronously
      const maxProjectsCount = (this.subscriptionAccessType === 'FULL' || this.isEnterpriseUser ? this.selectedPlan?.maxProjectsCount : this.lastSelectedPlan?.maxProjectsCount) || 0

      // Sort project memberships by organisation.createdAt
      const sortedMemberships = [...projectMemberships].sort((a, b) => {
        return new Date(b.project.organisation.createdAt) - new Date(a.project.organisation.createdAt)
      })

      // Process memberships based on maxProjectsCount
      return sortedMemberships.map((projectMembership, index) => {
        const isDisabled = index >= maxProjectsCount
        return {
          ...projectMembership,
          disabled: isDisabled
        }
      })
    },
    filteredProjectMemberships () {
      return this.projectMembershipsWithAccess?.filter(pm => pm.project)
    },
    hasOrganisations () {
      // by checking that the length is 0
      // we must wait for the organisations to be loaded
      // which is what we want
      return this.organisations?.length !== 0
    },
    hasProjects () {
      return this.projectMemberships?.length > 0
    },
    recentWorkflows () {
      // get recent workflows from localStorage and add projectName and other data
      const recentWorkflows = recentWorkflowsApi.getList()
      recentWorkflows.forEach((it, _) => {
        const membership = this.filteredProjectMemberships?.find(it2 => it2.project.id === it.projectId)
        const projectName = membership?.project?.name || 'Public'
        const organisationName = membership?.project?.organisation?.name || ''
        it.projectName = projectName
        it.organisationName = organisationName

        // Important: This line we need for updating recent workflows when current user is changing, because first time current user is null and after getting current user recent workflow not updating
        // eslint-disable-next-line no-unused-expressions
        this.currentUser
      })
      return recentWorkflows
    },
    hasRecentWorkflows () {
      return this.recentWorkflowsWithData?.length > 0
    },
    filteredAndSortedRecentWorkflowsWithData () {
      return [...this.recentWorkflowsWithData].filter((workflow) => {
        if (this.workflowsFilterBy === 'owned_by_me') {
          return this.isProjectOwner(workflow?.projectId)
        } else if (this.workflowsFilterBy === 'not_owned_by_me') {
          return !this.isProjectOwner(workflow?.projectId)
        }
        return true
      }).sort((a, b) => {
        if (this.workflowsSortBy === 'last_modified') {
          const dateA = a.updatedAt ? new Date(a.updatedAt) : 0
          const dateB = b.updatedAt ? new Date(b.updatedAt) : 0
          return dateB - dateA
        } else if (this.workflowsSortBy === 'last_created') {
          const dateA = a.createdAt ? new Date(a.createdAt) : 0
          const dateB = b.createdAt ? new Date(b.createdAt) : 0
          return dateB - dateA
        } else if (this.workflowsSortBy === 'alphabetically') {
          return a.name.localeCompare(b.name)
        }
        return 0
      })
    },
    filteredAndSortedProjectMemberships () {
      if (!this.filteredProjectMemberships) {
        return []
      }
      return [...this.filteredProjectMemberships]
        .filter((membership) => {
          if (this.projectsFilterBy === 'owned_by_me') {
            return this.isProjectOwner(membership?.project?.id)
          } else if (this.projectsFilterBy === 'not_owned_by_me') {
            return !this.isProjectOwner(membership?.project?.id)
          }
          return true
        })
        .sort((a, b) => {
          if (this.projectsSortBy === 'last_opened') {
            const dateA = a.lastLogin ? new Date(a.lastLogin) : 0
            const dateB = b.lastLogin ? new Date(b.lastLogin) : 0
            return dateB - dateA
          } else if (this.projectsSortBy === 'last_created') {
            const dateA = a.createdAt ? new Date(a.createdAt) : 0
            const dateB = b.createdAt ? new Date(b.createdAt) : 0
            return dateB - dateA
          } else if (this.projectsSortBy === 'alphabetically') {
            return a.project?.name?.localeCompare(b.project?.name)
          }
          return 0
        })
    }
  },
  watch: {
    workflowsSortBy (val) {
      writeStorage(WORKFLOWS_SORT_STORAGE_KEY, val)
    },
    projectsSortBy (val) {
      writeStorage(PROJECTS_SORT_STORAGE_KEY, val)
    },
    workflowsFilterBy (val) {
      writeStorage(WORKFLOWS_FILTER_STORAGE_KEY, val)
    },
    projectsFilterBy (val) {
      writeStorage(PROJECTS_FILTER_STORAGE_KEY, val)
    },
    recentWorkflows: {
      handler: async function (val, oldVal) {
        if (!this.isFirstRun && JSON.stringify(val) === JSON.stringify(oldVal)) {
          // no change
          return
        }
        if (!this.isFirstRun && !this.projectMemberships) {
          // wait for project memberships to be loaded
          // otherwise we'll fetch workflows twice
          return
        }
        if (this.isFirstRun) {
          this.isFirstRun = false
        }
        this.$loadingState.start()
        await this.fetchAndUpdateWorkflowsWithSummary()
        this.$loadingState.stop()
      },
      deep: true,
      immediate: true
    }
  },
  mounted () {
    if (
      ['trial', 'basic', 'pro'].includes(this.$route.query.plan)
    ) {
      this.welcomeToPlanName = this.$route.query.plan
      this.welcomeToPlanDialog = true
      this.$router.replace({ path: '/dashboard', query: {} })
    }
  }
}
</script>

<style scoped>
.text-new-button {
  color: var(--dark-3);
  font-size: 20px;
  line-height: 24px;
  letter-spacing: -0.6px;
}
</style>

<style>
.dashboard-grid-layout {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  width: 100%;
  gap: 12px;
}

.dashboard-card {
  padding: 12px;
  cursor: pointer;

  width: 100%;
  overflow: hidden;
  height: 206px;

  border-radius: 6px;
  border: 1px solid var(--light-1);
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

.dashboard-card-placeholder {
  cursor: default;
  box-shadow: none;
  background: linear-gradient(90deg, var(--light-1) 25%, var(--light-3) 50%, var(--light-1) 75%);
  background-size: 200% 100%;
  animation: dashboard-card-shimmer 1.4s ease-in-out infinite;
}

@keyframes dashboard-card-shimmer {
  from {
    background-position: 100% 0;
  }

  to {
    background-position: -100% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-card-placeholder {
    animation: none;
  }
}

.dashboard-container-alt {
  margin-top: 40px;
  padding-top: 43px;
  margin-left: -40px;
  margin-right: -40px;
  padding-left: 40px;
  padding-right: 40px;
  height: 100%;
  background-color: #F0EDEB;
}

[data-theme="dark"] .dashboard-container-alt {
  background-color: var(--light-2);
}
</style>
